
import { mixins, Options } from 'vue-class-component'
import BaseFormDialogMixin from '@/components/common/mixins/BaseFormDialogMixin.vue'
import { Prop, Watch } from 'vue-property-decorator'
import { IMedia, IRichMenuContent, IRichMenuTemplateCompact } from '@/utils/types'
import RichMenuTemplate from '@/components/rich-menus/selectors/RichMenuTemplate.vue'
import MediaUploader from '@/components/media-manager/MediaUploader.vue'
import { constant } from '@/utils/constants'
import cloneDeep from 'lodash/cloneDeep'
import { maska } from 'maska'
import CropperImageSelector from '@/components/tappable-area/selectors/CropperImageSelector.vue'
import UploadingProgress from '@/components/common/ui/UploadingProgress.vue'
import UploadApi from '@/api/upload'
import { now } from '@/utils/datetime'
import { UserModel } from '@/models/user-model'

@Options({
  components: { UploadingProgress, CropperImageSelector, RichMenuTemplate, MediaUploader },
  directives: { maska },
  emits: ['update:modelValue', 'onSubmitTemplate', 'onSubmitMenu'],
})
export default class RichMenuAssetModal extends mixins(BaseFormDialogMixin) {
  @Prop()
  menuData!: IRichMenuContent

  @Prop()
  selectedTemplate!: IRichMenuTemplateCompact

  get userInfo(): UserModel {
    return this.$store.getters.userInfo
  }

  errorImageMessage = ''
  file: File | null = null
  cropFiles: File[] = []
  cropperImageModalVisible = false

  form: IRichMenuContent = {
    label: '',
    rich_menu_alias_id: '',
    size: {
      width: constant.RICH_MENU_IMAGE.MAX_IMAGE_WIDTH,
      height: constant.RICH_MENU_IMAGE.MAX_IMAGE_HEIGHT,
    },
    tappable_area: [],
    image_url: '',
  }

  get requiredRule() {
    return {
      messageImage: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.common.messages.image'),
      }),
      label: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.rich_menu.label'),
      }),
    }
  }

  validate() {
    this.errorImageMessage = ''
    if (!this.form.image_url) {
      this.errorImageMessage = this.requiredRule.messageImage
    }

    return this.errorImageMessage === ''
  }

  async onSubmitTemplate(file) {
    if (file) {
      this.form.image_url = file.url || ''
      this.form.content_type = file.content_type
      this.form.image_width = file.image_width
      this.form.image_height = file.image_height
      if (file.image_width && file.image_height) {
        this.form.size = {
          width: file.image_width,
          height: file.image_height,
        }
      }
      if (file.tappable_area) {
        this.form.tappable_area = file.tappable_area
      }
      this.form.selected_template = file.selected_template
      this.$emit('onSubmitTemplate', this.form)
    }
  }

  onSubmit() {
    const isValid = this.validate()
    this.$refs.menuAssetForm
      .validate()
      .then(async (success: boolean) => {
        if (!success || !isValid) {
          return
        }

        this.form.tappable_area = this.menuData.tappable_area
        this.form.created_at = this.menuData.created_at || now(constant.DATETIME_FORMAT)
        this.form.created_by = this.menuData.created_by || this.userInfo.display_name

        this.$emit('onSubmitMenu', this.form)
      })
      .catch((error: unknown) => {
        console.log('error', error)
      })
  }

  async validateInputFile(file: File) {
    this.errorImageMessage = ''

    if (!file && this.form.image_url === '') {
      this.errorImageMessage = this.requiredRule.messageImage
      return false
    }

    if (file.size > constant.RICH_MENU_IMAGE.MAX_FILE_SIZE) {
      this.errorImageMessage = this.$t('validate.file_too_big', {
        placeholder: file.name,
      })

      return false
    }

    let result = true
    if (file) {
      result = await this.validateImageSize(file)
    }

    return result
  }

  async validateImageSize(file: File) {
    return new Promise<boolean>((resolve) => {
      const img = new Image()
      img.src = window.URL.createObjectURL(file)
      img.onload = (event: Event) => {
        const data = event.target as HTMLImageElement

        if (
          data.width < constant.RICH_MENU_IMAGE.MIN_IMAGE_WIDTH ||
          data.width > constant.RICH_MENU_IMAGE.MAX_IMAGE_WIDTH
        ) {
          this.errorImageMessage = this.$t('validate.image_width_size', {
            placeholder: file.name,
          })
          resolve(false)
          return
        }

        if (data.height < constant.RICH_MENU_IMAGE.MIN_IMAGE_HEIGHT) {
          this.errorImageMessage = this.$t('validate.image_height_size', {
            placeholder: file.name,
          })
          resolve(false)
          return
        }

        if (data.width / data.height < constant.RICH_MENU_IMAGE.MIN_ASPECT_RATIO) {
          this.errorImageMessage = this.$t('validate.image_aspect_ratio', {
            placeholder: file.name,
          })
          resolve(false)
          return
        }

        this.form.image_width = data.width
        this.form.image_height = data.height
        resolve(true)
      }
    })
  }

  async submitFile(file: File) {
    try {
      const uploaded = await UploadApi.uploadFile(file)
      if (uploaded) {
        this.form.image_url = uploaded.file_url_org || ''
        this.form.content_type = file.type
      }
    } catch (error) {
      console.log(error)
    }
  }

  onCloseModal() {
    this.cropperImageModalVisible = false
    this.errorImageMessage = ''
    this.file = null
    this.cropFiles = []
  }

  async compressToJpeg(file: File, maxBytes = 1_048_576): Promise<File> {
    return new Promise((resolve) => {
      const img = new Image()
      img.src = URL.createObjectURL(file)
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) { resolve(file); return }
        ctx.drawImage(img, 0, 0)

        const tryQuality = (quality: number) => {
          canvas.toBlob((blob) => {
            if (!blob) { resolve(file); return }
            if (blob.size > maxBytes && quality > 0.1) {
              tryQuality(Math.round((quality - 0.1) * 10) / 10)
              return
            }
            const fileName = file.name.replace(/\.[^.]+$/, '.jpg')
            resolve(new File([blob], fileName, { type: 'image/jpeg' }))
          }, 'image/jpeg', quality)
        }
        tryQuality(0.9)
      }
    })
  }

  async onCropImage(file: File, width: number, height: number) {
    this.form.image_width = width
    this.form.image_height = height
    this.cropperImageModalVisible = false

    // Compress to JPEG under LINE's 1 MB limit (rich menu specific)
    const compressed = await this.compressToJpeg(file)
    this.form.content_type = 'image/jpeg'
    this.cropFiles.push(compressed)
    await this.submitFile(compressed)
    this.onCloseModal()
  }

  async onSelectMedia(file: IMedia) {
    if (!file) return

    this.errorImageMessage = ''

    // Validate file size
    if (file.file_size && file.file_size > constant.RICH_MENU_IMAGE.MAX_FILE_SIZE) {
      this.errorImageMessage = this.$t('validate.file_too_big', {
        placeholder: file.name || file.url,
      })
      return
    }

    // Validate dimensions
    if (file.image_width && file.image_height) {
      if (
        file.image_width < constant.RICH_MENU_IMAGE.MIN_IMAGE_WIDTH ||
        file.image_width > constant.RICH_MENU_IMAGE.MAX_IMAGE_WIDTH
      ) {
        this.errorImageMessage = this.$t('validate.image_width_size', {
          placeholder: file.name || file.url,
        })
        return
      }
      if (file.image_height < constant.RICH_MENU_IMAGE.MIN_IMAGE_HEIGHT) {
        this.errorImageMessage = this.$t('validate.image_height_size', {
          placeholder: file.name || file.url,
        })
        return
      }
      if (file.image_width / file.image_height < constant.RICH_MENU_IMAGE.MIN_ASPECT_RATIO) {
        this.errorImageMessage = this.$t('validate.image_aspect_ratio', {
          placeholder: file.name || file.url,
        })
        return
      }
    }

    this.form.image_url = file.url || ''
    this.form.content_type = file.content_type
    this.form.image_width = file.image_width
    this.form.image_height = file.image_height
    if (file.image_width && file.image_height) {
      this.form.size = {
        width: file.image_width,
        height: file.image_height,
      }
    }
  }

  @Watch('file')
  async handleUpload() {
    if (!this.file) {
      return false
    }

    try {
      // Block oversized files immediately — cropping won't reduce file size enough
      if (this.file.size > constant.RICH_MENU_IMAGE.MAX_FILE_SIZE) {
        this.errorImageMessage = this.$t('validate.file_too_big', {
          placeholder: this.file.name,
        })
        this.file = null
        return false
      }

      if (!(await this.validateInputFile(this.file))) {
        await this.$q
          .dialog({
            title: this.$t('messages.confirm'),
            message: this.errorImageMessage,
            cancel: { flat: true, label: this.$t('messages.no') },
            ok: { flat: true, label: this.$t('messages.yes') },
            persistent: true,
          })
          .onOk(() => {
            this.errorImageMessage = ''
            this.cropperImageModalVisible = true
          })
          .onCancel(() => {
            this.onCloseModal()
          })
        return false
      }

      this.cropFiles.push(this.file)
      await this.submitFile(this.file)
      this.file = null
      this.cropFiles = []
    } catch (error) {
      console.log(error)
    }
  }

  async created() {
    this.form = { ...this.form, ...cloneDeep(this.menuData) }
    this.parentForm = this.form as any
    this.initForm = cloneDeep(this.parentForm)
  }
}
